import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useMemo,
    useState,
} from "react";
import { ScheduleReviewFailure } from "@common/schemas/domains/accounting/scheduleReview.schema";

interface ScheduleReviewOutcomeState {
    failureMessagesByApplicationId: ReadonlyMap<string, string>;
    recordFailures: (failures: ScheduleReviewFailure[]) => void;
    clearFailures: (applicationIds: string[]) => void;
}

const EMPTY_FAILURES: ReadonlyMap<string, string> = new Map();

const ScheduleReviewOutcomeContext = createContext<ScheduleReviewOutcomeState>({
    failureMessagesByApplicationId: EMPTY_FAILURES,
    recordFailures: () => undefined,
    clearFailures: () => undefined,
});

/**
 * Remembers why a draft failed to activate so the row can show the reason:
 * a failed draft stays a draft, so the refetched list carries nothing about
 * the attempt. Entries clear once the draft activates or is dismissed.
 */
export const ScheduleReviewOutcomeProvider = ({
    children,
}: {
    children: ReactNode;
}) => {
    const [failureMessagesByApplicationId, setFailureMessagesByApplicationId] =
        useState<ReadonlyMap<string, string>>(EMPTY_FAILURES);

    const recordFailures = useCallback((failures: ScheduleReviewFailure[]) => {
        if (failures.length === 0) {
            return;
        }
        setFailureMessagesByApplicationId((previous) => {
            const next = new Map(previous);
            for (const failure of failures) {
                next.set(failure.applicationId, failure.message);
            }
            return next;
        });
    }, []);

    const clearFailures = useCallback((applicationIds: string[]) => {
        setFailureMessagesByApplicationId((previous) => {
            if (!applicationIds.some((id) => previous.has(id))) {
                return previous;
            }
            const next = new Map(previous);
            for (const applicationId of applicationIds) {
                next.delete(applicationId);
            }
            return next;
        });
    }, []);

    const value = useMemo(
        () => ({
            failureMessagesByApplicationId,
            recordFailures,
            clearFailures,
        }),
        [clearFailures, failureMessagesByApplicationId, recordFailures],
    );

    return (
        <ScheduleReviewOutcomeContext.Provider value={value}>
            {children}
        </ScheduleReviewOutcomeContext.Provider>
    );
};

export function useScheduleReviewOutcome(): ScheduleReviewOutcomeState {
    return useContext(ScheduleReviewOutcomeContext);
}
